import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "../constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LucideTrash, LucidePlus, LucideEdit, LucideArrowLeft, LucideUser, LucideShare2, LucideX, LucidePencil, LucideEye, LucideChevronLeft, LucideChevronRight, LucideDownload, LucideSettings2, LucideCheck } from "lucide-react";
import { REPORT_FIELD_LABELS } from "../constants/reportFieldLabels";
import { bulkExport } from "@/lib/bulkExportService";
import useSubscriptionInfo from "@/hooks/useSubscriptionInfo";

const REPORTS_PAGE_SIZE = 10;

export const Class = () => {
  const { reportGroupId } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [classData, setClassData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [studentName, setStudentName] = useState("");
  const [studentOen, setStudentOen] = useState("");
  const [applyDefaults, setApplyDefaults] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharePermission, setSharePermission] = useState("view");
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null); // { id, label }

  const [reportsPage, setReportsPage] = useState(0);
  const [sortField, setSortField] = useState('oen');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalReports, setTotalReports] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [allReportsForExport, setAllReportsForExport] = useState([]);

  const [showDefaultsModal, setShowDefaultsModal] = useState(false);
  const [defaultsForm,      setDefaultsForm]      = useState({});
  const [savingDefaults,    setSavingDefaults]     = useState(false);
  const [defaultsError,     setDefaultsError]     = useState("");
  const [defaultsSaved,     setDefaultsSaved]     = useState(false);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSelected, setExportSelected] = useState(new Set());
  const [exportPdfs, setExportPdfs] = useState(true);
  const [exportCsv, setExportCsv] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null); // { done, total }
  const [exportDone, setExportDone] = useState(false);

  const { reportsUsed, maxReports, loading: subLoading } = useSubscriptionInfo();
  const atLimit = !subLoading && reportsUsed >= maxReports;

  const fetchClass = async () => {
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        sort: sortField,
        order: sortOrder,
        search: debouncedSearch,
        page: reportsPage,
        limit: REPORTS_PAGE_SIZE,
      });
      const res = await fetch(`${BACKEND_URL}api/report-groups/${reportGroupId}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch class");
      const data = await res.json();
      setClassData(data);
      setReports(data.reports || []);
      setTotalReports(data.totalReports ?? 0);
      setTotalPages(data.totalPages ?? 1);
      setFetchError(null);
    } catch (err) {
      console.error("Error fetching class:", err);
      setFetchError("Could not load this class. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 0 when sort or search changes
  useEffect(() => {
    setReportsPage(0);
  }, [sortField, sortOrder, debouncedSearch]);

  // Fetch whenever group, sort, search, or page changes
  useEffect(() => {
    fetchClass();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportGroupId, sortField, sortOrder, debouncedSearch, reportsPage]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!studentName) return;
    setCreating(true);
    setCreateError("");
    try {
      const token = await getToken();
      const body = { studentName, applyDefaults };
      if (studentOen.trim()) body.oen = studentOen.trim();
      const res = await fetch(`${BACKEND_URL}api/report-groups/${reportGroupId}/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      if (res.status === 403) {
        setCreateError("Report card quota exceeded. Please upgrade your plan.");
        return;
      }
      if (!res.ok) throw new Error("Failed to create report card");

      const newCard = await res.json();
      setStudentName("");
      setStudentOen("");
      setApplyDefaults(true);
      setShowForm(false);
      navigate(`/classes/${reportGroupId}/reports/${newCard._id}`);
    } catch (err) {
      console.error("Error creating report card:", err);
      setCreateError("Failed to create report card. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (card) => {
    if (!confirm(`Delete report card for ${card.studentName}?`)) return;
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/reports/${card._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports((prev) => prev.filter((r) => r._id !== card._id));
    } catch (err) {
      console.error("Error deleting report card:", err);
      toast.error("Failed to delete report card. Please try again.");
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    if (!shareEmail) return;
    setSharing(true);
    setShareError("");
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/shared-classes/${reportGroupId}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: shareEmail, permission: sharePermission }),
      });
      const body = await res.json();
      if (!res.ok) { setShareError(body.message || "Failed to share class"); return; }
      setShareEmail("");
      setSharePermission("view");
      if (body.pending) {
        toast.success("Share request submitted - awaiting admin approval.");
        setShowShareModal(false);
      } else {
        toast.success(`Class shared with ${shareEmail}.`);
        // Refresh class data to get updated sharedWith list
        await fetchClass();
      }
    } catch (err) {
      console.error("Error sharing class:", err);
      setShareError("Failed to share class");
    } finally {
      setSharing(false);
    }
  };

  const handleRemoveMember = (memberId, memberLabel) => {
    setConfirmRemove({ id: memberId, label: memberLabel });
  };

  const doRemoveMember = async () => {
    const { id: memberId, label: memberLabel } = confirmRemove;
    setConfirmRemove(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/shared-classes/${reportGroupId}/members/${memberId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      setClassData((prev) => ({
        ...prev,
        sharedWith: prev.sharedWith.filter((m) => {
          const mId = String(m.userId?._id ?? m.userId ?? m._id);
          return mId !== memberId;
        }),
      }));
      toast.success(`${memberLabel} has been removed from the class.`);
    } catch (err) {
      console.error("Error removing member:", err);
      toast.error("Failed to remove member. Please try again.");
    }
  };

  const handleUpdatePermission = async (memberId, newPermission) => {
    const previousData = classData;
    // Optimistic update - flip the toggle immediately
    setClassData((prev) => ({
      ...prev,
      sharedWith: prev.sharedWith.map((m) => {
        const mId = String(m.userId?._id ?? m.userId ?? m._id);
        return mId === memberId ? { ...m, permission: newPermission } : m;
      }),
    }));
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/shared-classes/${reportGroupId}/members/${memberId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ permission: newPermission }),
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`Permission updated to "${newPermission === "edit" ? "Can edit" : "View only"}".`);
    } catch (err) {
      console.error("Error updating permission:", err);
      setClassData(previousData); // revert on failure
      toast.error("Failed to update permission. Please try again.");
    }
  };

  const fieldLabels = REPORT_FIELD_LABELS[classData?.reportType] ?? [];

  const openDefaultsModal = () => {
    const existing = classData?.reportDefaults ?? {};
    const form = {};
    fieldLabels.forEach((f) => { form[f.key] = existing[f.key] ?? ""; });
    setDefaultsForm(form);
    setDefaultsError("");
    setDefaultsSaved(false);
    setShowDefaultsModal(true);
  };

  const handleSaveDefaults = async (e) => {
    e.preventDefault();
    setSavingDefaults(true);
    setDefaultsError("");
    setDefaultsSaved(false);
    try {
      const token = await getToken();
      // Only send fields that have a value; empty = don't sync
      const payload = Object.fromEntries(
        Object.entries(defaultsForm).filter(([, v]) => v.trim() !== "")
      );
      const res = await fetch(`${BACKEND_URL}api/report-groups/${reportGroupId}/report-defaults`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) { setDefaultsError(body.message || "Failed to save report defaults"); return; }
      setClassData((prev) => ({ ...prev, reportDefaults: body.reportDefaults }));
      setDefaultsSaved(true);
      setTimeout(() => setDefaultsSaved(false), 2500);
    } catch {
      setDefaultsError("Failed to save report defaults. Please try again.");
    } finally {
      setSavingDefaults(false);
    }
  };

  const openExportModal = async () => {
    try {
      const token = await getToken();
      const params = new URLSearchParams({ sort: sortField, order: sortOrder, limit: 0 });
      const res = await fetch(`${BACKEND_URL}api/report-groups/${reportGroupId}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch reports");
      const data = await res.json();
      const all = data.reports || [];
      setAllReportsForExport(all);
      setExportSelected(new Set(all.map((r) => r._id)));
    } catch {
      setAllReportsForExport(reports);
      setExportSelected(new Set(reports.map((r) => r._id)));
    }
    setExportPdfs(true);
    setExportCsv(false);
    setExporting(false);
    setExportProgress(null);
    setExportDone(false);
    setShowExportModal(true);
  };

  const toggleExportReport = (id) => {
    setExportSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleExport = async () => {
    const selected = allReportsForExport.filter((r) => exportSelected.has(r._id));
    if (selected.length === 0 || (!exportPdfs && !exportCsv)) return;
    setExporting(true);
    setExportDone(false);
    setExportProgress({ done: 0, total: (exportPdfs ? selected.length : 0) + (exportCsv ? 1 : 0) });
    try {
      const ok = await bulkExport({
        reports: selected,
        exportPdfs,
        exportCsv,
        className: [
          classData?.grade && `Grade ${classData.grade}`,
          classData?.schoolYear,
        ].filter(Boolean).join(' - ') || classData?.name,
        onProgress: (done, total) => setExportProgress({ done, total }),
      });
      if (ok) setExportDone(true);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="dashboard-content text-muted-foreground">Loading...</div>;
  if (fetchError) return <div className="dashboard-content"><div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fetchError}</div></div>;

  return (
    <div className="dashboard-content">
      <Link
        to="/classes"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <LucideChevronLeft size={14} />
        My Classes
      </Link>

      <div className="dashboard-welcome">
        <div>
          <h1 className="dashboard-heading">
            {[
              classData?.grade ? `Grade ${classData.grade}` : null,
              classData?.schoolYear || null,
            ].filter(Boolean).join(' · ') || classData?.name || 'Class'}
          </h1>
          {classData?.name && (
            <p className="dashboard-subheading">{classData.name}</p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {atLimit && (
            <span className="text-sm text-destructive">
              Report card limit reached.{" "}
              <Link to="/billing" className="underline">
                Upgrade
              </Link>
            </span>
          )}
          {reports.length > 0 && (
            <Button variant="outline" onClick={openExportModal}>
              <LucideDownload className="mr-2" size={16} />
              Export
            </Button>
          )}
          {fieldLabels.length > 0 && (
            <Button variant="outline" onClick={openDefaultsModal}>
              <LucideSettings2 className="mr-2" size={16} />
              Report Defaults
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowShareModal(true)}>
            <LucideShare2 className="mr-2" size={16} />
            Share
          </Button>
          <Button onClick={() => setShowForm((v) => !v)} disabled={atLimit}>
            <LucidePlus className="mr-2" size={16} />
            New Report Card
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="border border-green-200 rounded-lg p-4 bg-green-50 dark:bg-green-950/20 dark:border-green-900 mb-6 space-y-3">
          <h2 className="text-base font-semibold text-foreground">New Report Card</h2>
          <div className="flex flex-wrap gap-3">
            <div className="space-y-1 flex-1 min-w-[160px] max-w-xs">
              <Label>Student Name *</Label>
              <Input
                placeholder="e.g. Jane Smith"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1 flex-1 min-w-[140px] max-w-xs">
              <Label>OEN <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                placeholder="e.g. 123456789"
                value={studentOen}
                onChange={(e) => setStudentOen(e.target.value)}
              />
            </div>
          </div>
          {Object.keys(classData?.reportDefaults ?? {}).length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
              <input
                type="checkbox"
                checked={applyDefaults}
                onChange={(e) => setApplyDefaults(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              <span className="text-sm text-foreground">Apply class defaults to this report</span>
            </label>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={creating}>
              {creating ? "Creating..." : "Create"}
            </Button>
            <Button type="button" variant="outline" onClick={() => { setShowForm(false); setStudentOen(""); setApplyDefaults(true); }}>
              Cancel
            </Button>
          </div>
          {createError && <p className="text-sm text-red-600">{createError}</p>}
        </form>
      )}

      {/* Search + sort controls */}
      {(totalReports > 0 || searchQuery) && (
        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            placeholder="Search by name or OEN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={`${sortField}:${sortOrder}`}
            onChange={(e) => {
              const [field, ord] = e.target.value.split(':');
              setSortField(field);
              setSortOrder(ord);
            }}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground"
          >
            <option value="oen:asc">OEN (A–Z)</option>
            <option value="oen:desc">OEN (Z–A)</option>
            <option value="name:asc">Name (A–Z)</option>
            <option value="name:desc">Name (Z–A)</option>
            <option value="updatedAt:desc">Recently edited</option>
            <option value="updatedAt:asc">Oldest edited</option>
          </select>
        </div>
      )}

      {reports.length === 0 && !searchQuery && (
        <div className="text-center py-16 text-muted-foreground">
          <LucideUser size={40} className="mx-auto mb-3 opacity-40" />
          <p>No report cards yet. Add students to get started.</p>
        </div>
      )}

      {reports.length === 0 && searchQuery && (
        <div className="text-center py-12 text-muted-foreground">
          <p>No students match &quot;{searchQuery}&quot;.</p>
        </div>
      )}

      <div className="space-y-2">
        {reports.map((card) => (
          <div key={card._id} className="border border-border rounded-lg p-4 bg-primary/5 flex items-center justify-between">
            <Link to={`/classes/${reportGroupId}/reports/${card._id}`} className="flex-1 min-w-0">
              <div className="flex items-center gap-3 px-3 py-1.5">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <LucideUser size={18} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground leading-tight">{card.studentName}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {card.data?.oen?.trim()
                      ? `OEN ${card.data.oen}`
                      : <span className="italic opacity-60">OEN not set</span>}
                  </p>
                </div>
              </div>
            </Link>
            <div className="flex gap-2">
              <Link to={`/classes/${reportGroupId}/reports/${card._id}`}>
                <Button size="sm">
                  <LucideEdit className="mr-1" size={14} />
                  Edit
                </Button>
              </Link>
              <Button variant="destructive" size="sm" onClick={() => handleDelete(card)}>
                <LucideTrash className="mr-1" size={14} />
                Delete
              </Button>
            </div>
          </div>
        ))}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReportsPage((p) => p - 1)}
              disabled={reportsPage === 0}
              className="gap-1"
            >
              <LucideChevronLeft size={14} />
              Prev
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {reportsPage + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setReportsPage((p) => p + 1)}
              disabled={reportsPage === totalPages - 1}
              className="gap-1"
            >
              Next
              <LucideChevronRight size={14} />
            </Button>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => { if (!exporting) setShowExportModal(false); }}
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[80vh]">
            <div className="flex items-start justify-between px-6 pt-6 pb-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <LucideDownload size={16} className="text-primary" />
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground leading-tight">Export Reports</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {allReportsForExport.length} student{allReportsForExport.length !== 1 ? "s" : ""} in this class
                  </p>
                </div>
              </div>
              {!exporting && (
                <button
                  onClick={() => setShowExportModal(false)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors mt-0.5 shrink-0"
                >
                  <LucideX size={15} />
                </button>
              )}
            </div>
            <div className="h-px bg-border mx-6 shrink-0" />

            <div className="px-6 py-5 space-y-5 overflow-y-auto">
              {!exportDone ? (
                <>
                  {/* Student selection */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground">Select students</p>
                      <div className="flex gap-2">
                        <button
                          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                          onClick={() => setExportSelected(new Set(allReportsForExport.map((r) => r._id)))}
                        >
                          All
                        </button>
                        <button
                          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                          onClick={() => setExportSelected(new Set())}
                        >
                          None
                        </button>
                      </div>
                    </div>
                    <div className="space-y-1 max-h-48 overflow-y-auto rounded-lg border border-border p-2">
                      {allReportsForExport.map((r) => (
                        <label
                          key={r._id}
                          className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={exportSelected.has(r._id)}
                            onChange={() => toggleExportReport(r._id)}
                            disabled={exporting}
                            className="accent-primary"
                          />
                          <span className="text-sm text-foreground">{r.studentName}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Export type */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Export as</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setExportPdfs((v) => !v)}
                        disabled={exporting}
                        className={[
                          "flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg border text-sm font-medium transition-colors",
                          exportPdfs
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border text-muted-foreground hover:border-muted-foreground/40",
                        ].join(" ")}
                      >
                        PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => setExportCsv((v) => !v)}
                        disabled={exporting}
                        className={[
                          "flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg border text-sm font-medium transition-colors",
                          exportCsv
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border text-muted-foreground hover:border-muted-foreground/40",
                        ].join(" ")}
                      >
                        CSV
                      </button>
                    </div>
                    {!('showDirectoryPicker' in window) && (
                      <p className="text-xs text-muted-foreground">
                        Files will be downloaded as a .zip (folder saving is not supported in this browser).
                      </p>
                    )}
                  </div>

                  {/* Progress */}
                  {exporting && exportProgress && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Generating...</span>
                        <span>{exportProgress.done} / {exportProgress.total}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-200"
                          style={{ width: `${(exportProgress.done / exportProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    className="w-full"
                    onClick={handleExport}
                    disabled={exporting || exportSelected.size === 0 || (!exportPdfs && !exportCsv)}
                  >
                    {exporting ? "Exporting..." : `Export ${exportSelected.size} report${exportSelected.size !== 1 ? "s" : ""}`}
                  </Button>
                </>
              ) : (
                <div className="text-center py-4 space-y-2">
                  <p className="text-sm font-medium text-foreground">Export complete</p>
                  <p className="text-xs text-muted-foreground">
                    {exportSelected.size} report{exportSelected.size !== 1 ? "s" : ""} saved successfully.
                  </p>
                  <Button variant="outline" className="mt-2" onClick={() => setShowExportModal(false)}>
                    Close
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => { if (!confirmRemove) { setShowShareModal(false); setShareError(""); } }}
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <LucideShare2 size={15} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground leading-tight">Share &quot;{classData?.name}&quot;</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Invite colleagues to collaborate on this class.</p>
                </div>
              </div>
              <button
                onClick={() => { setShowShareModal(false); setShareError(""); setConfirmRemove(null); }}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
              >
                <LucideX size={15} />
              </button>
            </div>

            <div className="h-px bg-border shrink-0" />

            {/* Two-column body */}
            <div className="flex divide-x divide-border min-h-0">

              {/* LEFT: Invite form */}
              <div className="flex-1 p-6 space-y-4">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Invite</p>
                <form onSubmit={handleShare} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">Email address</label>
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      placeholder="colleague@school.ca"
                      required
                      className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    />
                  </div>

                  {/* Permission cards */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">Permission</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setSharePermission("view")}
                        className={[
                          "flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition-all",
                          sharePermission === "view"
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground",
                        ].join(" ")}
                      >
                        <LucideEye size={16} />
                        <span>View only</span>
                        <span className="text-[10px] font-normal opacity-60 leading-tight text-center">Can read reports</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSharePermission("edit")}
                        className={[
                          "flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition-all",
                          sharePermission === "edit"
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground",
                        ].join(" ")}
                      >
                        <LucidePencil size={16} />
                        <span>Can edit</span>
                        <span className="text-[10px] font-normal opacity-60 leading-tight text-center">Full write access</span>
                      </button>
                    </div>
                  </div>

                  {shareError && <p className="text-xs text-destructive">{shareError}</p>}
                  <Button type="submit" disabled={sharing || !shareEmail} className="w-full h-10 text-sm">
                    {sharing ? "Sending..." : "Send invite"}
                  </Button>
                </form>
              </div>

              {/* RIGHT: Members list */}
              <div className="w-[420px] p-6 flex flex-col gap-4 shrink-0">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest shrink-0">
                  Members{classData?.sharedWith?.length > 0 ? ` (${classData.sharedWith.length})` : ""}
                </p>

                {!classData?.sharedWith?.length ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mb-2">
                      <LucideUser size={18} className="text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">No members yet</p>
                    <p className="text-[11px] text-muted-foreground/60 mt-0.5">Invite someone on the left</p>
                  </div>
                ) : (
                  <div className="space-y-2.5 overflow-y-auto max-h-72">
                    {classData.sharedWith.map((member) => {
                      const memberId = member.userId?._id ?? member.userId ?? member._id;
                      const memberLabel = member.userId?.email ?? member.userId?.name ?? String(memberId);
                      const initials = memberLabel.slice(0, 2).toUpperCase();
                      return (
                        <div key={String(memberId)} className="p-3 rounded-xl border border-border group hover:bg-muted/30 transition-colors">
                          {/* Top row: avatar + email + remove */}
                          <div className="flex items-center gap-3">
                            <div className={[
                              "w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold uppercase shrink-0",
                              member.permission === "edit"
                                ? "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                                : "bg-primary/10 text-primary",
                            ].join(" ")}>
                              {initials}
                            </div>
                            <p className="text-xs text-foreground flex-1 min-w-0 truncate" title={memberLabel}>{memberLabel}</p>
                            <button
                              onClick={() => handleRemoveMember(String(memberId), memberLabel)}
                              className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all shrink-0"
                            >
                              <LucideX size={13} />
                            </button>
                          </div>

                          {/* Bottom row: permission toggle */}
                          <div className="mt-2.5 ml-11">
                            <div className="inline-flex items-center rounded-lg border border-border overflow-hidden">
                              <button
                                type="button"
                                disabled={member.permission === "view"}
                                onClick={() => handleUpdatePermission(String(memberId), "view")}
                                className={[
                                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors",
                                  member.permission === "view"
                                    ? "bg-primary text-primary-foreground cursor-default"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer",
                                ].join(" ")}
                              >
                                <LucideEye size={11} />
                                View only
                              </button>
                              <div className="w-px self-stretch bg-border shrink-0" />
                              <button
                                type="button"
                                disabled={member.permission === "edit"}
                                onClick={() => handleUpdatePermission(String(memberId), "edit")}
                                className={[
                                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors",
                                  member.permission === "edit"
                                    ? "bg-primary text-primary-foreground cursor-default"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer",
                                ].join(" ")}
                              >
                                <LucidePencil size={11} />
                                Can edit
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}
      {/* Remove Member Confirmation */}
      {confirmRemove && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmRemove(null)} />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-destructive/10 flex items-center justify-center shrink-0">
                <LucideX size={16} className="text-destructive" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Remove member?</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  <span className="font-medium text-foreground">{confirmRemove.label}</span> will immediately lose access to this class and all its reports.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmRemove(null)}
                className="h-9 px-4 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={doRemoveMember}
                className="h-9 px-4 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Defaults Modal */}
      {showDefaultsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
            onClick={() => { if (!savingDefaults) setShowDefaultsModal(false); }}
          />
          <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="flex items-start justify-between px-6 py-5 border-b border-border shrink-0">
              <div>
                <h2 className="text-base font-semibold text-foreground">Report Defaults</h2>
                <p className="text-sm text-muted-foreground mt-1 leading-snug">
                  Fill in the values you want applied to every report in this class. Leave a field blank to skip it.
                </p>
              </div>
              {!savingDefaults && (
                <button
                  onClick={() => setShowDefaultsModal(false)}
                  className="ml-4 mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                >
                  <LucideX size={16} />
                </button>
              )}
            </div>

            {/* Scrollable form body */}
            <form onSubmit={handleSaveDefaults} className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
                {Array.from(new Set(fieldLabels.map((f) => f.step))).map((step) => (
                  <div key={step}>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 pb-2 border-b border-border">
                      {step}
                    </p>
                    <div className="space-y-3">
                      {fieldLabels.filter((f) => f.step === step).map((f) => {
                        const hasValue = (defaultsForm[f.key] ?? "").trim() !== "";
                        return (
                          <div
                            key={f.key}
                            className={[
                              "rounded-lg border px-4 py-3 transition-colors",
                              hasValue
                                ? "bg-green-50 dark:bg-green-950/30 border-green-300 dark:border-green-700"
                                : "bg-background border-input",
                            ].join(" ")}
                          >
                            <label
                              htmlFor={`default-${f.key}`}
                              className={[
                                "block text-sm font-medium mb-1.5",
                                hasValue ? "text-green-800 dark:text-green-300" : "text-foreground",
                              ].join(" ")}
                            >
                              {f.label}
                            </label>
                            <input
                              id={`default-${f.key}`}
                              type="text"
                              value={defaultsForm[f.key] ?? ""}
                              onChange={(e) =>
                                setDefaultsForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                              }
                              placeholder={`Enter ${f.label.toLowerCase()}…`}
                              className={[
                                "h-9 w-full rounded-md border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 transition-colors",
                                hasValue
                                  ? "bg-white dark:bg-green-950/40 border-green-300 dark:border-green-700"
                                  : "bg-background border-input",
                              ].join(" ")}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 pb-6 pt-4 border-t border-border shrink-0 space-y-3">
                <p className="text-xs text-muted-foreground">
                  Filled fields will be written to all {reports.length} report{reports.length !== 1 ? "s" : ""} in this class. Blank fields will not be changed.
                </p>
                {defaultsError && (
                  <p className="text-sm text-destructive">{defaultsError}</p>
                )}
                {defaultsSaved && (
                  <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-1.5">
                    <LucideCheck size={14} strokeWidth={2.5} />
                    Saved and applied to all reports.
                  </p>
                )}
                <Button type="submit" disabled={savingDefaults} className="w-full" size="lg">
                  {savingDefaults ? "Saving…" : "Save & Apply to All Reports"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
